import { AnyExtension, Extension, Extensions } from "@tiptap/core";
import { Mention, MentionOptions } from "@tiptap/extension-mention";
import { Placeholder } from "@tiptap/extensions";
import { PluginKey } from "@tiptap/pm/state";
import { StarterKit } from "@tiptap/starter-kit";
import { ChatMarkdown } from "./ChatMarkdown";

type ChatComposerExtensionsArgs = {
  getPlaceholder: () => string;
  mentionSuggestion: MentionOptions["suggestion"];
  emojiSuggestion: AnyExtension;
  isSuggestionListOpen: () => boolean;
  onSubmit: () => void;
  onOpenLinkInput: () => void;
};

const MENTION_SUGGESTION_KEY = new PluginKey("chatMentionSuggestion");

const MENTION_CHAR = "@";
const LINK_PROTOCOL = "https";

const ChatMention = Mention.extend({
  renderMarkdown: (node) =>
    `${MENTION_CHAR}${node.attrs?.label ?? node.attrs?.id ?? ""}`,
});

const createChatKeymap = ({
  isSuggestionListOpen,
  onSubmit,
  onOpenLinkInput,
}: Pick<
  ChatComposerExtensionsArgs,
  "isSuggestionListOpen" | "onSubmit" | "onOpenLinkInput"
>) =>
  Extension.create({
    name: "chatKeymap",
    // Runs before the core/list Enter handlers: Enter always sends (like Slack)
    priority: 101,

    addKeyboardShortcuts() {
      const openLinkInput = () => {
        onOpenLinkInput();
        return true;
      };

      return {
        // An open @/: list handles Enter itself to pick the highlighted item
        Enter: ({ editor }) => {
          if (editor.view.composing || isSuggestionListOpen()) return false;

          onSubmit();
          return true;
        },
        // New bullet in lists, new line in code, otherwise a line break
        "Shift-Enter": ({ editor }) => {
          if (editor.isActive("codeBlock")) {
            return editor.commands.newlineInCode();
          }

          if (editor.isActive("listItem")) {
            const isEmptyItem =
              editor.state.selection.$from.parent.content.size === 0;
            if (isEmptyItem) return editor.commands.liftListItem("listItem");

            return editor.commands.splitListItem("listItem");
          }

          return false;
        },
        "Mod-Shift-x": ({ editor }) => editor.commands.toggleStrike(),
        "Mod-Shift-c": ({ editor }) => editor.commands.toggleCode(),
        "Mod-Shift-9": ({ editor }) => editor.commands.toggleBlockquote(),
        "Mod-Shift-u": openLinkInput,
      };
    },
  });

export const createChatComposerExtensions = ({
  getPlaceholder,
  mentionSuggestion,
  emojiSuggestion,
  isSuggestionListOpen,
  onSubmit,
  onOpenLinkInput,
}: ChatComposerExtensionsArgs): Extensions => [
  StarterKit.configure({
    heading: false,
    horizontalRule: false,
    underline: false,
    dropcursor: false,
    link: {
      autolink: false,
      openOnClick: false,
      linkOnPaste: true,
      defaultProtocol: LINK_PROTOCOL,
    },
  }),
  Placeholder.configure({ placeholder: getPlaceholder }),
  ChatMention.configure({
    HTMLAttributes: { class: "mention" },
    suggestion: {
      ...mentionSuggestion,
      char: MENTION_CHAR,
      pluginKey: MENTION_SUGGESTION_KEY,
    },
  }),
  emojiSuggestion,
  createChatKeymap({ isSuggestionListOpen, onSubmit, onOpenLinkInput }),
  ChatMarkdown,
];
